@if (canEdit) {
  <div class="arcgis-push-container" data-testid="arcgis-push">
    @if (loading) {
      <div class="loading-container">
        <mat-spinner diameter="24"></mat-spinner>
      </div>
    }

    @if (!loading && status) {
      <div class="section">
        <div class="section-head">
          <div>
            <h3 class="section-title">ArcGIS push</h3>
            <p class="subtitle">
              Sends the points, lines and areas captured on daily reports to your organization's ArcGIS feature layers.
            </p>
          </div>
          <div [class]="'status status--' + statusView.tone" data-testid="arcgis-push-status">
            <span class="dot"></span>{{ statusView.label }}
          </div>
        </div>

        @if (statusView.detail) {
          <p class="error-hint">{{ statusView.detail }}</p>
        }

        @if (!connectionReady) {
          <div class="notice" data-testid="arcgis-push-no-connection">
            Your organization has no ArcGIS connection yet. An organization admin can add it under
            Organization → ArcGIS connection.
          </div>
        }

        @if (status.target) {
          <div class="metadata">
            <span>{{ status.pushed.features }} feature(s) from {{ status.pushed.reports }} report(s) on the layers</span>
            @if (status.target.last_pushed_at) {
              <span class="dot-sep">·</span>
              <span>Last push {{ status.target.last_pushed_at | dateFormat: 'very-long' }}</span>
            }
            @if (status.pending > 0) {
              <span class="dot-sep">·</span>
              <span>{{ status.pending }} pending</span>
            }
            @if (status.failed > 0) {
              <span class="dot-sep">·</span>
              <span class="failed-count" data-testid="arcgis-push-failed-count">{{ status.failed }} failed</span>
            }
          </div>
        }
      </div>

      <form [formGroup]="form" class="section" (ngSubmit)="save()">
        <div class="toggle-row">
          <div>
            <div class="label text-type-13 uppercase">Push to ArcGIS</div>
            <span class="hint">{{ removalHint }}</span>
          </div>
          <mat-slide-toggle formControlName="enabled" color="primary" [disableRipple]="true" data-testid="arcgis-push-enabled" />
        </div>

        <div class="field-group">
          <div class="label text-type-13 uppercase">Send reports when they are</div>
          <mat-radio-group formControlName="trigger" class="radio-group" color="primary" aria-label="When to push">
            <mat-radio-button value="submit" color="primary">Submitted</mat-radio-button>
            <mat-radio-button value="approve" color="primary">Approved</mat-radio-button>
            <mat-radio-button value="save" color="primary" data-testid="arcgis-push-trigger-save">Saved, including drafts</mat-radio-button>
          </mat-radio-group>
          @if (form.controls.trigger.value === 'save') {
            <span class="hint" data-testid="arcgis-push-save-hint">
              Drafts appear on the layers as they are edited, with report_status "draft". Use this for projects where
              reports are often left unsubmitted.
            </span>
          }
        </div>

        <div class="field-group">
          <div class="label text-type-13 uppercase">Feature layers</div>
          @if (orgLayersOffered) {
            <mat-radio-group formControlName="layer_source" class="radio-group" color="primary" aria-label="Which layers" data-testid="arcgis-push-layer-source">
              <mat-radio-button value="org" color="primary">Organization layers</mat-radio-button>
              <mat-radio-button value="project" color="primary">This project's layers</mat-radio-button>
            </mat-radio-group>
          }
          @if (usesOrgLayers) {
            <span class="hint source-hint">Set by an organization admin under Organization → ArcGIS connection.</span>
            @for (field of layerFields; track field.kind) {
              <div class="org-layer" [attr.data-testid]="'arcgis-push-org-' + field.kind">
                <div class="label text-type-13 uppercase">{{ field.label }}</div>
                <div class="org-layer-url">{{ layerUrl(field.control) || 'Not set' }}</div>
                <div class="layer-meta">
                  <span class="hint">{{ field.hint }}</span>
                  @if (layerCheck(field.kind)?.ok) {
                    <span class="check-ok" [title]="readyTitle(field.kind)" [attr.data-testid]="'arcgis-push-check-' + field.kind">Ready</span>
                  }
                </div>
                @if (layerCheck(field.kind); as check) {
                  @if (!check.ok || check.warnings.length) {
                    <div class="layer-check">
                      @for (e of check.errors; track $index) {
                        <span class="error-hint">{{ e }}</span>
                      }
                    </div>
                  }
                }
              </div>
            }
          } @else {
            <span class="hint source-hint">Paste each layer's URL from the Sitenotes_FieldRecords service. Leave a layer empty to skip it.</span>
            @for (field of layerFields; track field.kind) {
              <div class="layer-row">
                <app-text-input
                  [parentFormGroup]="form"
                  [controlName]="field.control"
                  [label]="field.label"
                  [error]="urlError(field.control)"
                  [maxLength]="1024"
                  placeholder="https://…/FeatureServer/0"
                  type="url"
                  class="w-full"
                />
                <div class="layer-meta" [class.layer-meta--after-error]="!!urlError(field.control)">
                  <span class="hint">{{ field.hint }}</span>
                  @if (layerCheck(field.kind)?.ok) {
                    <span class="check-ok" [title]="readyTitle(field.kind)" [attr.data-testid]="'arcgis-push-check-' + field.kind">Ready</span>
                  }
                </div>
                @if (layerCheck(field.kind); as check) {
                  @if (!check.ok || check.warnings.length) {
                    <div class="layer-check" [attr.data-testid]="'arcgis-push-check-' + field.kind">
                      @for (e of check.errors; track $index) {
                        <span class="error-hint">{{ e }}</span>
                      }
                      @for (w of check.warnings; track $index) {
                        <span class="hint">{{ w }}</span>
                      }
                    </div>
                  }
                }
              </div>
            }
          }
          @if (preflight && !preflight.ok && preflight.message) {
            <span class="error-hint" data-testid="arcgis-push-preflight-message">{{ preflight.message }}</span>
          }
        </div>

        <div class="form-actions">
          <button
            mat-flat-button
            color="primary"
            type="submit"
            [disabled]="saving || !connectionReady || form.invalid"
            data-testid="arcgis-push-save"
          >
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
          <button
            mat-stroked-button
            color="primary"
            type="button"
            [disabled]="testing || !connectionReady || !hasLayerUrl"
            (click)="testLayers()"
            data-testid="arcgis-push-test"
          >
            {{ testing ? 'Checking…' : 'Test layers' }}
          </button>
        </div>
      </form>

      @if (status.target) {
        <div class="section">
          <div class="section-head section-head--rule">
            <h3 class="section-title">Failed pushes</h3>
            <div class="actions-row">
              <button mat-button color="primary" type="button" [disabled]="retrying || !status.failed || !status.target.enabled" (click)="retry()" data-testid="arcgis-push-retry">
                Retry failed
              </button>
              <button mat-button color="primary" type="button" [disabled]="backfilling || !status.target.enabled" (click)="backfill()" data-testid="arcgis-push-backfill">
                Push existing reports
              </button>
            </div>
          </div>
          @if (!status.target.enabled && status.failures.length) {
            <p class="hint">Turn the push on to retry these.</p>
          }
          @if (!status.failures.length) {
            <p class="hint">No failed pushes.</p>
          } @else {
            <table class="failures" data-testid="arcgis-push-failures">
              <thead>
                <tr><th>Report</th><th>Error</th><th>Tries</th><th>Failed</th></tr>
              </thead>
              <tbody>
                @for (f of status.failures; track f.job_id) {
                  <tr>
                    <td>
                      @if (failureLink(f); as link) {
                        <a [routerLink]="link" [queryParams]="failureQueryParams(f)">{{ f.report_date | dateFormat: 'MMM D, YYYY' }}</a>
                      } @else {
                        {{ f.report_date | dateFormat: 'MMM D, YYYY' }}
                      }
                    </td>
                    <td class="message" [title]="f.message">{{ f.message }}</td>
                    <td>{{ f.attempts }}</td>
                    <td>{{ f.failed_at | dateFormat: 'very-long' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>
      }
    }
  </div>
}
